import React from 'react';
import { type ResultData, ResultTemplateKey } from 'types/result';
import { t } from 'i18next';

export const formatTextWithBold = (text: string): string => {
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
};

export function parseBoldText(
  text: string,
  BoldComponent: React.ComponentType<React.PropsWithChildren<{ key?: string | number }>>
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the bold marker
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Add bold text
    const BoldElement = BoldComponent as React.ComponentType<
      React.PropsWithChildren<{ key?: string | number }>
    >;
    parts.push(React.createElement(BoldElement, { key: key++ }, match[1]));
    lastIndex = regex.lastIndex;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  // If no matches found, return original text
  if (parts.length === 0) {
    return [text];
  }

  return parts;
}

export const stripBoldMarkdown = (text: string): string => {
  return text.replace(/\*\*(.*?)\*\*/g, '$1');
};

export const formatDateTime = (
  timestamp: string,
  options?: { fullYear?: boolean; separator?: string }
): string => {
  const { fullYear = true, separator } = options ?? {};
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const yearStr = fullYear ? yyyy : yyyy.toString().slice(-2);
  const sep = separator ?? ` `;
  const suffix = separator ? '' : ` ${t('clock')}`;
  return `${dd}.${mm}.${yearStr}${sep}${hh}:${min}${suffix}`;
};

export const formatReadableDate = (date: string | Date): string => {
  let d: Date;
  if (typeof date === 'string') {
    const dotFormat = /^\d{2}\.\d{2}\.\d{4}$/;
    if (dotFormat.test(date)) return date;
    d = new Date(date);
  } else {
    d = date;
  }
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

export const formatMonthYear = (isoDate: string): string => {
  const match = /^(\d{4})-(\d{2})/.exec(isoDate);
  return match ? `${match[2]}/${match[1]}` : '';
};

export const formatReadableTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${min} ${t('clock')}`;
};

export const isDateToday = (date: string | Date): boolean => {
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) return false;
  const today = new Date();
  return (
    dateObj.getDate() === today.getDate() &&
    dateObj.getMonth() === today.getMonth() &&
    dateObj.getFullYear() === today.getFullYear()
  );
};

export const isDateYesterday = (date: string | Date): boolean => {
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) return false;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    dateObj.getDate() === yesterday.getDate() &&
    dateObj.getMonth() === yesterday.getMonth() &&
    dateObj.getFullYear() === yesterday.getFullYear()
  );
};

export const formatRelativeDaySuffix = (date: string | Date): string => {
  if (isDateToday(date)) return ` (${t('today')})`;
  if (isDateYesterday(date)) return ` (${t('yesterday')})`;
  return '';
};

export const formatDateToDisplayString = (date: string, relative: boolean = false): string => {
  if (!date) return '';
  if (isDateToday(date)) return t('today');
  if (isDateYesterday(date)) return t('yesterday');
  return relative ? `am ${formatReadableDate(date)}` : formatReadableDate(date);
};

export const formatDuration = (durationSeconds: number): string => {
  const roundedDurationSeconds = Math.round(durationSeconds);
  const minutes = Math.floor(roundedDurationSeconds / 60);
  const seconds = roundedDurationSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const formatElapsedTime = (durationSeconds: number): string => {
  const roundedSeconds = Math.max(0, Math.round(durationSeconds));
  const hours = Math.floor(roundedSeconds / 3600);
  const minutes = Math.floor((roundedSeconds % 3600) / 60);
  const seconds = roundedSeconds % 60;
  return [hours, minutes, seconds].map((part) => part.toString().padStart(2, '0')).join(':');
};

export const toIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export function ensureString(value?: string | number | null): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }
  return '';
}

export const getSectionManualNote = (
  sectionKey: ResultTemplateKey,
  result: ResultData | null | undefined
): string => {
  if (!result) return '';

  switch (sectionKey) {
    case ResultTemplateKey.Anamnesis:
      return result.anamnesis;
    case ResultTemplateKey.Findings:
      return result.findings;
    case ResultTemplateKey.Diagnosis:
      return result.diagnosis ?? '';
    case ResultTemplateKey.Therapy:
      return result.therapy;
    case ResultTemplateKey.Procedure:
      return result.procedure;
    case ResultTemplateKey.Comment:
      return result.comment ?? '';
    default:
      return '';
  }
};

export function buildTranscriptText(result?: ResultData): string {
  const list = result?.transcripts ?? [];
  if (!list.length) return '';
  const sorted = [...list].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
  return sorted.map((entry) => entry.text).join('\n');
}

// Auth storage utilities
export * from './authStorage';

// Environment utilities
export * from './environment';

// Number utilities
export * from './number';
